import type { ZervantSettings } from '../types/zervantSettings'
import type { FieldInterface, ZervantSettingsModel } from '../types/zervantSettingsModel'

const INVOICE_MODEL_NAME = 'models.invoice.sales'
const ESTIMATE_MODEL_NAME = 'models.estimate'

// Fields the estimate keeps for itself: the quote title and the quote validity days.
const FIELD_NAMES_NOT_COPIED: readonly string[] = ['title', 'paymentTime']

// The settings endpoint stores documents verbatim, so a stored field can lack `default` or
// `visible` even though the shared types mark `visible` as required.
interface LooseField extends Omit<FieldInterface, 'visible'> {
  visible?: boolean
}

interface LooseModel extends Omit<ZervantSettingsModel, 'columns' | 'fields'> {
  columns?: string[]
  fields: LooseField[]
}

type SettingsWithModels = Partial<ZervantSettings>

// Until the Quote settings section launches, a settings save writes the invoice model into the
// estimate model as well, but only while the stored estimate is still a copy of the stored invoice.
// Removed with SILC-3209.
export const copyInvoiceModelToEstimate = <TPayload extends SettingsWithModels>(
  payload: TPayload,
  cachedSettings: SettingsWithModels | undefined,
): TPayload => {
  const { models } = payload
  if (!models) {
    return payload
  }

  const payloadInvoice = findModelByName(models, INVOICE_MODEL_NAME)
  const payloadEstimate = findModelByName(models, ESTIMATE_MODEL_NAME)
  const cachedInvoice = findModelByName(cachedSettings?.models, INVOICE_MODEL_NAME)
  const cachedEstimate = findModelByName(cachedSettings?.models, ESTIMATE_MODEL_NAME)

  if (!payloadInvoice || !payloadEstimate || !cachedInvoice || !cachedEstimate) {
    return payload
  }

  if (!isEstimateCopyOfInvoice(cachedInvoice, cachedEstimate)) {
    return payload
  }

  const copiedEstimate = getEstimateWithInvoiceValues(payloadInvoice, payloadEstimate)

  return {
    ...payload,
    models: models.map((model) => (model === payloadEstimate ? copiedEstimate : model)),
  }
}

const findModelByName = (models: ZervantSettingsModel[] | undefined, name: string): ZervantSettingsModel | undefined =>
  models?.find((model) => model.name === name)

const isFieldCopied = (field: LooseField): boolean => !FIELD_NAMES_NOT_COPIED.includes(field.name)

const findFieldByName = (fields: LooseField[], name: string): LooseField | undefined =>
  fields.find((field) => field.name === name)

const isEstimateCopyOfInvoice = (invoice: LooseModel, estimate: LooseModel): boolean => {
  const invoiceColumns = invoice.columns ?? []
  const estimateColumns = estimate.columns ?? []
  if (
    invoiceColumns.length !== estimateColumns.length ||
    invoiceColumns.some((column, index) => column !== estimateColumns[index])
  ) {
    return false
  }

  // Fields present in only one of the models are ignored. With duplicate names the first one wins.
  return invoice.fields.filter(isFieldCopied).every((invoiceField) => {
    const estimateField = findFieldByName(estimate.fields, invoiceField.name)
    if (!estimateField || invoiceField !== findFieldByName(invoice.fields, invoiceField.name)) {
      return true
    }

    return (
      (invoiceField.default ?? '') === (estimateField.default ?? '') &&
      (invoiceField.visible ?? true) === (estimateField.visible ?? true)
    )
  })
}

const getEstimateWithInvoiceValues = (invoice: LooseModel, estimate: ZervantSettingsModel): ZervantSettingsModel => ({
  ...estimate,
  columns: [...(invoice.columns ?? [])],
  fields: estimate.fields.map((estimateField) => {
    const invoiceField = isFieldCopied(estimateField) ? findFieldByName(invoice.fields, estimateField.name) : undefined
    if (!invoiceField) {
      return estimateField
    }

    const copiedField: FieldInterface = { ...estimateField, default: invoiceField.default ?? '' }
    const { visible } = invoiceField

    // Copy `visible` only when the invoice field stores its own value.
    return 'visible' in invoiceField && visible !== undefined ? { ...copiedField, visible } : copiedField
  }),
})
